/**
 * Draagt dit object nog de naam van de palet-tegel waar het vandaan komt?
 *
 * Sharp legt eerst de STROOM vast met lege dozen en geeft ze pas daarna een
 * naam (H8, fig. 8.4 op p207-208: "A handoff diagram — whether it's lot of
 * work, or a little bit of work, draw one box"). Wij vulden bij het slepen de
 * naam van de tegel in — "Step", "Merge point" — en dat is geen naam maar een
 * soortaanduiding. Twee gevolgen die allebei in de weg zitten: het canvas
 * staat vol met hetzelfde woord, en de tweede doos is volgens de
 * duplicaat-detectie een kloon van de eerste, dus je krijgt bij elke doos de
 * samenvoeg-vraag.
 *
 * Vandaar deze afleiding: is de naam gelijk aan wat een palet-tegel voor dit
 * type + stereotype zou invullen, dan is 'ie nog niet benoemd. Puur afgeleid,
 * dus ook objecten die er al stonden vallen eronder, en zodra de modelleur
 * iets anders intypt is het een naam als elke andere.
 */
import type { Concept } from "@ae/domain";
import { getRegistry, resolveBilingual } from "../../registry";

const norm = (s: string | undefined): string => (s ?? "").trim().toLowerCase();

const zelfdeSet = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((s) => b.includes(s));

export function heeftStandaardnaam(
  concept: Pick<Concept, "typeId" | "name" | "stereotypes">,
): boolean {
  const nl = norm(concept.name?.nl);
  const en = norm(concept.name?.en);
  if (!nl && !en) return true; // naamloos is ook "nog niet benoemd"
  const stereotypes = concept.stereotypes ?? [];
  const kandidaten: string[] = [];

  const reg = getRegistry();
  for (const groep of reg.listPaletteGroups()) {
    for (const entry of groep.entries ?? []) {
      if (entry.type_id !== concept.typeId) continue;
      if (!zelfdeSet(entry.default_stereotypes ?? [], stereotypes)) continue;
      if (entry.label_override) {
        kandidaten.push(norm(entry.label_override.nl), norm(entry.label_override.en));
      }
    }
  }
  // Ook het kale type-label: een drop zonder eigen tegel-tekst gebruikt dat.
  const type = reg.getElementType(concept.typeId);
  if (type?.label) kandidaten.push(norm(type.label.nl), norm(type.label.en));
  // En het stereotype-label, voor paden die dát als naam invullen.
  for (const s of stereotypes) {
    const st = reg.getStereotype(s);
    if (st?.label) kandidaten.push(norm(st.label.nl), norm(st.label.en));
  }

  return kandidaten.some((k) => k.length > 0 && (k === nl || k === en));
}

/** De naam zoals je 'm toont: leeg zolang 'ie de tegel-naam draagt. */
export function getoondeNaam(
  concept: Pick<Concept, "typeId" | "name" | "stereotypes">,
  lang: "nl" | "en",
): string {
  if (heeftStandaardnaam(concept)) return "";
  return resolveBilingual(concept.name, lang);
}
